import { Injectable } from '@angular/core';
import { FeatureFlagService } from '@core/services/statsig';

export interface CiamV6NudgeConfig {
  enabled: boolean;
  placement: string[];
  title: string;
  body: string;
  primaryCta: string;
  secondaryCta: string;
  bonusPoints: number;
  homeDisplayDelaySeconds: number;
  mandatoryDisplayDelaySeconds: number;
  dismissalCooldowns: number[];
  dismissalCooldownUnit: string;
  mandatoryEnabled: boolean;
  mandatoryTitle: string;
  mandatoryBody: string;
  mandatoryCta: string;
}

const DEFAULT_NUDGE: CiamV6NudgeConfig = {
  enabled: false,
  placement: [],
  title: 'Faster Sign In',
  body: "Skip the password. Sign in with a quick email code instead—it's faster and more secure.",
  primaryCta: 'SWITCH NOW',
  secondaryCta: 'NOT YET',
  bonusPoints: 0,
  homeDisplayDelaySeconds: 3,
  mandatoryDisplayDelaySeconds: 0.5,
  //empty, not [7, 3, 1]: an uninitialised SDK must never be able to produce a mandatory state
  dismissalCooldowns: [],
  dismissalCooldownUnit: 'days',
  //absent key means no hard block. The reverse default would lock real customers out over a typo
  mandatoryEnabled: false,
  mandatoryTitle: 'Update Your Sign In',
  mandatoryBody:
    "We're moving to a more secure sign in. Set up your quick email code now to keep using your account.",
  mandatoryCta: 'CONTINUE'
};

@Injectable()
export class CiamFlagsService {
  constructor(private readonly featureFlags: FeatureFlagService) {}

  private isV6Auth0Ux(): boolean {
    if (!this.featureFlags.isInitialized) {
      return true;
    }
    return !!this.featureFlags.getConfigValue('ciam_v6_auth_mode', 'v6_auth0_ux', true);
  }

  isCiamV6Com(): boolean {
    return this.isV6Auth0Ux();
  }

  isIdentifierFirst(): boolean {
    return this.isCiamV6Com();
  }

  isAuth0Signup(): boolean {
    return this.isCiamV6Com();
  }

  isJitMigration(): boolean {
    return this.getNudgeConfig().enabled;
  }

  getNudgeConfig(): CiamV6NudgeConfig {
    if (!this.featureFlags.isInitialized) {
      return { ...DEFAULT_NUDGE };
    }
    const enabled = this.featureFlags.getConfigValue('ciam_v6_nudge', 'enabled', false);
    const placementRaw = this.featureFlags.getConfigValue<string[] | string>(
      'ciam_v6_nudge',
      'nudge_placement',
      []
    );
    const placement = Array.isArray(placementRaw)
      ? placementRaw.map((p) => String(p).trim()).filter(Boolean)
      : [];
    const title = String(this.featureFlags.getConfigValue('ciam_v6_nudge', 'title', DEFAULT_NUDGE.title) ?? '');
    const body = String(this.featureFlags.getConfigValue('ciam_v6_nudge', 'body', DEFAULT_NUDGE.body) ?? '');
    const primaryCta = String(
      this.featureFlags.getConfigValue('ciam_v6_nudge', 'primary_cta', DEFAULT_NUDGE.primaryCta) ?? ''
    );
    const secondaryCta = String(
      this.featureFlags.getConfigValue('ciam_v6_nudge', 'secondary_cta', DEFAULT_NUDGE.secondaryCta) ?? ''
    );
    const bonusPoints = Number(this.featureFlags.getConfigValue('ciam_v6_nudge', 'bonus_points', 0) ?? 0);
    const mandatoryTitle = String(
      this.featureFlags.getConfigValue('ciam_v6_nudge', 'mandatory_title', DEFAULT_NUDGE.mandatoryTitle) ?? ''
    );
    const mandatoryBody = String(
      this.featureFlags.getConfigValue('ciam_v6_nudge', 'mandatory_body', DEFAULT_NUDGE.mandatoryBody) ?? ''
    );
    const mandatoryCta = String(
      this.featureFlags.getConfigValue('ciam_v6_nudge', 'mandatory_cta', DEFAULT_NUDGE.mandatoryCta) ?? ''
    );
    const cooldownUnit = String(
      this.featureFlags.getConfigValue('ciam_v6_nudge', 'dismissal_cooldown_unit', DEFAULT_NUDGE.dismissalCooldownUnit) ??
        ''
    );
    return {
      enabled: !!enabled,
      placement,
      title: title || DEFAULT_NUDGE.title,
      body: body || DEFAULT_NUDGE.body,
      primaryCta: primaryCta || DEFAULT_NUDGE.primaryCta,
      secondaryCta: secondaryCta || DEFAULT_NUDGE.secondaryCta,
      bonusPoints: Number.isFinite(bonusPoints) ? bonusPoints : 0,
      homeDisplayDelaySeconds: this.readDelay('home_display_delay_seconds', DEFAULT_NUDGE.homeDisplayDelaySeconds),
      mandatoryDisplayDelaySeconds: this.readDelay(
        'mandatory_display_delay_seconds',
        DEFAULT_NUDGE.mandatoryDisplayDelaySeconds
      ),
      dismissalCooldowns: this.readCooldowns(),
      dismissalCooldownUnit: cooldownUnit || DEFAULT_NUDGE.dismissalCooldownUnit,
      mandatoryEnabled: !!this.featureFlags.getConfigValue('ciam_v6_nudge', 'mandatory_enabled', false),
      mandatoryTitle: mandatoryTitle || DEFAULT_NUDGE.mandatoryTitle,
      mandatoryBody: mandatoryBody || DEFAULT_NUDGE.mandatoryBody,
      mandatoryCta: mandatoryCta || DEFAULT_NUDGE.mandatoryCta
    };
  }

  /** Read as floating point so fractional delays such as 0.5 survive. */
  private readDelay(key: string, fallback: number): number {
    const raw = Number(this.featureFlags.getConfigValue<number>('ciam_v6_nudge', key, fallback));
    return Number.isFinite(raw) && raw >= 0 ? raw : fallback;
  }

  /**
   * Empty on anything other than a usable array, matching CiamFlagsService.ReadCooldowns in MAUI. The
   * cadence is entirely operator-controlled, so a missing or malformed key means nobody chose one and
   * the caller declines to show. Defaulting to a real ladder here would invent a dismissal budget, and
   * the showing after that invented budget is spent is the hard block.
   */
  private readCooldowns(): number[] {
    const raw = this.featureFlags.getConfigValue<number[] | unknown>('ciam_v6_nudge', 'dismissal_cooldowns', []);
    if (!Array.isArray(raw)) {
      return [];
    }
    return raw.map((v) => Number(v)).filter((v) => Number.isFinite(v) && v > 0);
  }

  isSocialMigration(): boolean {
    if (!this.featureFlags.isInitialized) {
      return false;
    }
    return !!this.featureFlags.getConfigValue('ciam_v6_session_migration_config', 'enabled', false);
  }

  getSocialMigrationRetryHours(): number {
    const hours = this.featureFlags.getConfigValue<number>('ciam_v6_session_migration_config', 'retry_interval_hours', 1);
    return typeof hours === 'number' && hours > 0 ? hours : 1;
  }

  isAuth0PasswordlessSignup(): boolean {
    if (!this.featureFlags.isInitialized) {
      return true;
    }
    return (
      this.isV6Auth0Ux() &&
      // Default must match CiamV6AuthMode.IsEmailSignupEnabled (email_signup_enabled).
      this.featureFlags.getConfigValue('ciam_v6_auth_mode', 'email_signup_enabled', true)
    );
  }
}
